'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const floatingCards = [
  {
    id: 'card_pricing',
    title: 'From $3',
    subtitle: 'Per Ride',
    icon: 'CurrencyDollarIcon' as const,
    position: 'top-20 right-10',
    delay: 'delay-100'
  },
  {
    id: 'card_eta',
    title: '2 min',
    subtitle: 'Unlock Time',
    icon: 'ClockIcon' as const,
    position: 'top-1/3 right-20',
    delay: 'delay-200'
  },
  {
    id: 'card_rating',
    title: '4.9★',
    subtitle: 'User Rating',
    icon: 'StarIcon' as const,
    position: 'bottom-1/3 right-16',
    delay: 'delay-300'
  }];


  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Gradient Background */}
      <div className="absolute inset-0 gradient-hero opacity-95" />
      
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Content */}
        <div className="text-left space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-dark border border-white/20">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-sm font-body font-medium text-white">
              Available 24/7 in 50+ Cities
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl font-display font-bold text-white leading-tight">
            Ride Electric,
            <br />
            <span className="text-accent">Ride Smart</span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl text-white/80 font-body max-w-xl">
            Unlock eco-friendly electric scooters in seconds. Affordable, convenient, and sustainable urban mobility at your fingertips.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              href="/booking"
              className="btn-primary inline-flex items-center justify-center gap-2 text-lg">
              
              <Icon name="BoltIcon" size={20} />
              Find a Scooter Now
            </Link>
            <button className="btn-secondary inline-flex items-center justify-center gap-2 text-lg">
              <Icon name="PlayIcon" size={20} />
              See How It Works
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex items-center gap-8 pt-4">
            <div className="flex items-center gap-2">
              <Icon name="CheckBadgeIcon" variant="solid" size={24} className="text-success" />
              <span className="text-sm text-white/70 font-body">Verified Scooters</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="ShieldCheckIcon" variant="solid" size={24} className="text-success" />
              <span className="text-sm text-white/70 font-body">Safe & Insured</span>
            </div>
          </div>
        </div>

        {/* Right Content - Phone Mockup with Floating Cards */}
        <div className="relative lg:h-[600px] flex items-center justify-center">
          {/* Phone Mockup */}
          <div className="relative z-20 w-[280px] h-[560px] bg-white rounded-[3rem] shadow-2xl border-8 border-gray-800 overflow-hidden float">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-3xl" />
            <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1f361932b-1764846461652.png"
                alt="ScootRide app interface showing map with available electric scooters and booking screen"
                className="w-full h-full object-cover rounded-2xl" />
              
            </div>
          </div>

          {/* Floating Cards */}
          {mounted && floatingCards.map((card) =>
          <div
            key={card.id}
            className={`absolute ${card.position} glass-dark p-4 rounded-2xl shadow-glow-md min-w-[140px] float ${card.delay}`}>
            
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <Icon name={card.icon} size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-lg font-display font-bold text-white">{card.title}</p>
                  <p className="text-xs text-white/60 font-body">{card.subtitle}</p>
                </div>
              </div>
            </div>
          )}

          {/* Decorative Glow */}
          <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full scale-75 -z-10" />
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDownIcon" size={32} className="text-white/50" />
      </div>
    </section>);

}